/* eslint-disable complexity */
import upperFirst from 'lodash/upperFirst';
import { DealType, AuthorTypeOptionsWithRealtor, Rooms } from '@search/filter-enums/enums';
import { seoSearchCommon, seoSearchCommonJoin, SeoSearchCommonProps } from './seoSearchCommon';

export function seoSearchHome({ params, geos, offersCount, offersSummary, region }: SeoSearchCommonProps) {
    const segment = params.segments;

    const {
        dealTypeText,
        offersCountText,
        geoText,
        regionText,
        nearMetroText,
        balconyText,
        seoMortgage,
        priceMaxText,
        rentPaymentText,
        parkingText,
        generateFaqTexts,
        minPriceText,
        isNonCapitalRegion,
        priceSeo
    } = seoSearchCommon({ params, geos, offersCount, offersSummary, region });

    const isDacha = priceSeo?.seg === 'dachu';
    const isDomVDerevne = priceSeo?.seg === 'v-derevne';

    const isStandalone = segment?.houseCategory?.[0] === 'STANDALONE';
    const isPartOfHouse = segment?.houseCategory?.[0] === 'PART_OF_HOUSE';

    // TODO: коттедж учитывать ?
    const isCottage = segment?.water?.[0] === 'HAS_ANY' &&
        segment?.heating?.[0] === 'HAS_ANY' &&
        segment?.sewerage?.[0] === 'HAS_ANY' &&
        segment?.electricity?.[0] === 'HAS_ANY' &&
        isStandalone &&
        segment?.landStatus?.every(item => item === 'DNP' || item === 'SNT' || item === 'IZHS');

    const isNonCapitalSell = params.dealType === DealType.SELL && isNonCapitalRegion;

    const { houseText, isDefaultCategory } = getHouseCategoryText(params, { isCottage, isDacha, isNonCapitalSell });
    const landStatusText = getHouseLandStatusText(params, isCottage);

    let authorText = '';
    const authorFirst = segment?.author?.[0];

    if (authorFirst === AuthorTypeOptionsWithRealtor.AGENCY) authorText = 'от\u00A0агентства';
    if (authorFirst === AuthorTypeOptionsWithRealtor.OWNER) authorText = 'от\u00A0собственника';

    let communicationsText = '';

    if (segment?.electricity?.[0] && segment?.water?.[0] && ! isCottage) {
        communicationsText = 'с\u00A0водой и \u00A0электричеством';
    } else if (segment?.gas?.[0] && segment?.water?.[0] && ! isCottage) {
        communicationsText = 'с\u00A0газом и\u00A0водой';
    } else if (segment?.gas?.[0] && segment?.heating?.[0] && ! isCottage) {
        communicationsText = 'с\u00A0отоплением и \u00A0газом';
    } else {
        if (segment?.electricity?.[0] && ! isCottage) communicationsText = 'с\u00A0электричеством';
        if (segment?.water?.[0] && ! isCottage) communicationsText = 'с\u00A0водой';
        if (segment?.gas?.[0]) communicationsText = 'с\u00A0газом';
        if (segment?.heating?.[0] && ! isCottage) communicationsText = 'с\u00A0отоплением';
    }
    if (segment?.sewerage?.[0] && ! isCottage) communicationsText = 'с\u00A0канализацией';
    if (segment?.communications) communicationsText = 'с\u00A0коммуникациями';

    let roomsText = '';
    const room = segment?.rooms?.[0];

    if (room === Rooms.ROOM_1) roomsText = 'с\u00A0комнатой';
    if (room === Rooms.ROOM_2) roomsText = 'с\u00A02-мя комнатами';
    if (room === Rooms.ROOM_3) roomsText = 'с\u00A03-мя комнатами';
    if (room === Rooms.ROOM_4 || room === Rooms.ROOM_5_AND_MORE) roomsText = 'с\u00A04-мя или\u00A0более комнатами';

    let bathroomLocationText = '';
    let furnitureText = '';

    if (segment?.furniture?.includes('NO')) furnitureText = 'без мебели';
    if (segment?.furniture?.includes('ALL')) furnitureText = 'с\u00A0мебелью';

    if (segment?.bathroomLocation?.[0] === 'INSIDE') bathroomLocationText = 'с\u00A0санузлом в\u00A0доме';

    let floorText: { accusative: string; plural: string; nominative: string; genitive: string } | undefined;

    if (segment?.floorsTotalMax === 1) {
        floorText = { accusative: 'одноэтажный', plural: 'одноэтажных', nominative: 'одноэтажный', genitive: 'одноэтажного' };
        if (isDacha) {
            floorText.accusative = 'одноэтажную';
            floorText.genitive = 'одноэтажной';
            floorText.nominative = 'одноэтажная';
        }
        if (isDefaultCategory) floorText.genitive = 'одноэтажных';
    }
    if (segment?.floorsTotalMin === 2 && segment?.floorsTotalMax === 2) {
        floorText = { accusative: 'двухэтажный', plural: 'двухэтажных', nominative: 'двухэтажный', genitive: 'двухэтажного' };
        if (isDacha) {
            floorText.accusative = 'двухэтажную';
            floorText.genitive = 'двухэтажной';
            floorText.nominative = 'двухэтажная';
        }
        if (isDefaultCategory) floorText.genitive = 'двухэтажных';
    }
    if (segment?.floorsTotalMin === 3 && segment?.floorsTotalMax === 3) {
        floorText = { accusative: 'трехэтажный', plural: 'трехэтажных', nominative: 'трехэтажный', genitive: 'трехэтажного' };
        if (isDacha) {
            floorText.accusative = 'трехэтажную';
            floorText.genitive = 'трехэтажной';
            floorText.nominative = 'трехэтажная';
        }
        if (isDefaultCategory) floorText.genitive = 'трехэтажных';
    }

    let petsAllowed = {
        accusative: '',
        plural: ''
    };

    if (params?.livingTenantOptions && params?.livingTenantOptions.includes('PETS_ALLOWED')) {
        petsAllowed = {
            accusative: ', в котором можно жить с животными',
            plural: ', в которых можно жить с животными'
        };
    }

    if (! params?.livingTenantOptions || params?.livingTenantOptions?.length > 1) {
        petsAllowed = {
            accusative: '',
            plural: ''
        };
    }

    const prefixCommon = seoSearchCommonJoin(
        isDacha || isDomVDerevne ? undefined : priceMaxText,
        nearMetroText,
        landStatusText,
        authorText,
        communicationsText,
        roomsText,
        balconyText,
        bathroomLocationText,
        furnitureText,
        rentPaymentText,
        seoMortgage,
        parkingText,
        geoText
    );
    const allCommon = `${prefixCommon}${regionText ? ' ' : ''}${regionText}`;

    const pageTitle = `${seoSearchCommonJoin(
        upperFirst(dealTypeText.verb),
        floorText?.accusative,
        houseText.accusative,
        isStandalone ? 'для постоянного проживания' : undefined,
        isDomVDerevne ? ' в деревне' : undefined,
        allCommon
    )}${petsAllowed.accusative}`;

    const metaPrefix = `${seoSearchCommonJoin(
        upperFirst(dealTypeText.verb),
        floorText?.accusative,
        houseText.accusative,
        isStandalone ? 'для постоянного проживания' : undefined,
        isDomVDerevne ? ' в деревне' : undefined
    )}${petsAllowed.accusative ? `${petsAllowed.accusative},` : ''} ${allCommon}`;

    let metaTitle = `${metaPrefix}\u00A0— ${
        offersCountText} о ${dealTypeText.prepositional} ${
        seoSearchCommonJoin(
            floorText?.plural,
            isPartOfHouse ? houseText.genitive : houseText.plural,
            furnitureText,
            landStatusText
        )} недорого: планировки, цены и\u00A0фото\u00A0— m2.ru`;

    let metaDescription = `🏠 ${metaPrefix}. ➜ В\u00A0каталоге недвижимости ${
        offersCountText} о\u00A0${dealTypeText.prepositional} ${
        seoSearchCommonJoin(
            floorText?.genitive,
            houseText.genitive,
            furnitureText,
            landStatusText,
            regionText
        )}. ✔ Описание и\u00A0фото. ✔ Отзывы покупателей. ✔ Расположение на\u00A0карте.`;

    if (isNonCapitalSell) {
        metaTitle = `${metaPrefix}, объявления о\u00A0${dealTypeText.prepositional} ${
            seoSearchCommonJoin(
                floorText?.plural,
                isPartOfHouse ? houseText.genitive : houseText.plural,
                furnitureText,
                landStatusText
            )} недорого, планировки, цены и\u00A0фото на m2.ru`;

        const description = `${seoSearchCommonJoin(
            upperFirst(dealTypeText.proposal),
            floorText?.accusative,
            houseText.accusative,
            isStandalone ? 'для постоянного проживания' : undefined,
            isDomVDerevne ? ' в деревне' : undefined
        )}${petsAllowed.accusative ? `${petsAllowed.accusative},` : ''} ${allCommon}`;

        metaDescription = `${description} по доступной цене. В\u00A0каталоге недвижимости ${
            offersCountText} о\u00A0${dealTypeText.prepositional} ${
            seoSearchCommonJoin(
                floorText?.genitive,
                houseText.genitive,
                furnitureText,
                landStatusText,
                minPriceText,
                allCommon
            )} с описанием объектов, планировок, фото. ✔ Отзывы покупателей. ✔ Расположение на\u00A0карте.`;
    }

    const relativeTitle = seoSearchCommonJoin(
        upperFirst(dealTypeText.verb),
        'похожий',
        floorText?.accusative,
        houseText.accusative,
        allCommon
    );

    const additionalPostfixText = { plural: petsAllowed?.plural, accusative: petsAllowed?.accusative };

    return {
        metaTitle,
        metaDescription,
        pageTitle,
        relativeTitle,
        faqTexts: generateFaqTexts({ prefixSegmentText: floorText, segmentText: houseText, commonPostfixText: allCommon, additionalPostfixText })
    };
}

const getHouseCategoryText = (
    params: SeoSearchCommonProps['params'],
    {
        isCottage,
        isDacha,
        isNonCapitalSell
    }: {
        isCottage: boolean;
        isDacha: boolean;
        isNonCapitalSell: boolean;
    }
) => {
    let houseText = {
        accusative: 'дом',
        plural: isNonCapitalSell ? 'частных домов' : 'загородных домов',
        genitive: isNonCapitalSell ? 'частных домов' : 'загородных частных домов',
        nominative: 'дом'
    };

    const hc = params.segments?.houseCategory?.[0];

    if (hc === 'TOWNHOUSE') houseText = { accusative: 'таунхаус', plural: 'таунхауса', genitive: 'таунхауса', nominative: 'таунхаус' };
    if (hc === 'DUPLEX') houseText = { accusative: 'дуплекс', plural: 'дуплекса', genitive: 'дуплекса', nominative: 'дуплекс' };
    if (hc === 'PART_OF_HOUSE') houseText = { accusative: 'часть дома', plural: 'части дома', genitive: 'частички', nominative: 'часть дома' };
    if (isCottage) houseText = { accusative: 'коттедж', plural: 'коттеджей', genitive: 'коттеджа', nominative: 'коттедж' };
    if (isDacha) houseText = { accusative: 'дачу', plural: 'дач', genitive: 'дачи', nominative: 'дача' };

    return {
        houseText,
        isDefaultCategory: houseText.accusative === 'дом'
    };
};

const getHouseLandStatusText = (params: SeoSearchCommonProps['params'], isCottage: boolean) => {
    let landStatusText = '';
    const ls = new Set(params.segments?.landStatus ?? []);

    if (ls.has('IZHS')) landStatusText = 'ИЖС';
    if (ls.has('DNP')) landStatusText = 'СНТ, ДНП';
    if (ls.has('AGRICULTURAL')) landStatusText = 'ЛПХ';
    if (ls.has('IZHS') && ls.has('DNP') && ls.has('SNT')) landStatusText = 'для\u00A0ПМЖ';
    if (isCottage) landStatusText = '';

    return landStatusText;
};
